import './HomescreenFreeTrialSection.css';
import '@rio-cloud/iframe-resizer/child'; // Required for resizing when included as iframe

import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../../../configuration/setup/store';
import { FreeTrialButton } from './FreeTrialButton';
import { freeTrialStatusThunk } from './freeTrialStatus.thunk';
import manPointingRight from './images/man_pointing_right.webp';
import { getIsEligibleForFreeTrial } from './redux/freeTrial.redux';

const LETTER_CONTAINER_CLASS = 'homescreen-free-trial-letters';
const LETTER_CLASS = 'homescreen-free-trial-letter';

function splitTextIntoAnimatedLetters(container: HTMLElement) {
    const text = container.textContent;
    if (!text || container.dataset.animatedText === text) {
        return;
    }

    container.textContent = '';
    container.dataset.animatedText = text;

    Array.from(text).forEach(character => {
        if (character === ' ') {
            container.appendChild(document.createTextNode(character));
            return;
        }

        const letterElement = document.createElement('span');
        letterElement.className = LETTER_CLASS;
        letterElement.textContent = character;
        container.appendChild(letterElement);
    });
}

function animateLetters(): Animation[] {
    document.querySelectorAll<HTMLElement>(`.${LETTER_CONTAINER_CLASS}`).forEach(container => {
        splitTextIntoAnimatedLetters(container);
    });

    const letters = Array.from(document.querySelectorAll<HTMLElement>(`.${LETTER_CLASS}`));
    if (!letters.length) {
        return [];
    }

    const prefersReducedMotion =
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
        letters.forEach(letter => {
            letter.style.opacity = '1';
        });
        return [];
    }

    const initialDelayMs = 1000;
    const revealDurationMs = 1000;
    const letterStaggerMs = 100;
    const repeatDelayMs = 2000;
    const cycleDurationMs = initialDelayMs + repeatDelayMs + revealDurationMs + (letters.length - 1) * letterStaggerMs;

    return letters
        .map((letter, index) => {
            const revealStart = (initialDelayMs + index * letterStaggerMs) / cycleDurationMs;
            const revealEnd = Math.min(revealStart + revealDurationMs / cycleDurationMs, 1);

            letter.getAnimations().forEach(animation => {
                animation.cancel();
            });

            if (typeof letter.animate !== 'function') {
                letter.style.opacity = '1';
                return null;
            }

            return letter.animate(
                [
                    { opacity: 0, offset: 0 },
                    { opacity: 0, offset: revealStart },
                    { opacity: 1, offset: revealEnd },
                    { opacity: 1, offset: 1 },
                ],
                {
                    duration: cycleDurationMs,
                    easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
                    fill: 'both',
                    iterations: Number.POSITIVE_INFINITY,
                }
            );
        })
        .filter((animation): animation is Animation => animation !== null);
}

export function HomescreenFreeTrialSection() {
    const showSection = useSelector(getIsEligibleForFreeTrial);
    const dispatch = useAppDispatch();
    const { locale } = useIntl();

    useEffect(() => {
        dispatch(freeTrialStatusThunk);
    }, [dispatch]);

    useEffect(() => {
        if (!showSection) {
            return;
        }

        const animations = animateLetters();

        return () => {
            animations.forEach(animation => {
                animation.cancel();
            });
        };
    }, [showSection, locale]);

    if (!showSection) {
        return <div style={{ height: 0 }} />;
    }

    return (
        <div className='bg-primary'>
            <div className='display-flex-ls flex-1-1-ls padding-top-2pct position-relative overflow-hidden'>
                <div className='container-fluid display-flex-ls flex-1-1-ls fluid-default'>
                    <div className='row display-flex-ls flex-wrap-ls flex-1-1-ls justify-content-center-ls'>
                        <div className='col-xs-12 margin-bottom-0 display-flex-ls'>
                            <div className='display-flex-ls flex-1-1-ls flex-column-ls'>
                                <div className='width-100pct text-color-white padding-x-5pct'>
                                    <div className='width-100pct max-width-800 rounded-circle bg-white aspect-ratio-1 position-absolute top-0 left-50pct translate-x-50pct-y-50pct opacity-30 blur-50' />
                                    <div className='display-flex gap-50 justify-content-center align-items-end'>
                                        <img
                                            className='img-responsive max-width-250 max-width-300-md position-relative display-none-xs display-none-ls display-block-sm'
                                            src={manPointingRight}
                                            alt='Man pointing right'
                                        />
                                        <div className='position-relative padding-top-2pct'>
                                            <div className='text-size-h1 text-bold margin-bottom-5 display-flex gap-20'>
                                                <div>
                                                    <span className='rioglyph rioglyph-ok-sign' />
                                                </div>
                                                <div className={LETTER_CONTAINER_CLASS}>
                                                    <FormattedMessage
                                                        id={'marketplace.freeTrial.homeScreenAnimation1'}
                                                    />
                                                </div>
                                            </div>
                                            <div className='text-size-h1 text-bold margin-bottom-5 display-flex gap-20'>
                                                <div>
                                                    <span className='rioglyph rioglyph-ok-sign' />
                                                </div>
                                                <div className={LETTER_CONTAINER_CLASS}>
                                                    <FormattedMessage
                                                        id={'marketplace.freeTrial.homeScreenAnimation2'}
                                                    />
                                                </div>
                                            </div>
                                            <div className='text-size-h1 text-bold margin-bottom-5 display-flex gap-20'>
                                                <div>
                                                    <span className='rioglyph rioglyph-ok-sign' />
                                                </div>
                                                <div className={LETTER_CONTAINER_CLASS}>
                                                    <FormattedMessage
                                                        id={'marketplace.freeTrial.homeScreenAnimation3'}
                                                    />
                                                </div>
                                            </div>
                                            <div className='max-width-50vw-xs max-width-250 scale-150 transform-origin-top-left margin-top-50 padding-bottom: 17px'>
                                                <div className='buyButton'>
                                                    <FreeTrialButton />
                                                </div>
                                            </div>
                                            <div className='max-width-700 text-size-12 margin-y-50'>
                                                <FormattedMessage id={'marketplace.freeTrial.homeScreen'} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>&nbsp;</div>
                    {/* force rendering margin */}
                </div>
            </div>
        </div>
    );
}
